/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import ColorRefinements from './color-refinements'
import SizeRefinements from './size-refinements'
import RadioRefinements from './radio-refinements'
import CheckboxRefinements from './checkbox-refinements'
import LinkRefinements from './link-refinements'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

import {HStack} from '@chakra-ui/layout'
import {
    Text,
    Button,
    Menu,
    MenuButton,
    MenuList,
    Stack,
    useMultiStyleConfig,
    Flex,
    Skeleton,
} from '@chakra-ui/react'
import ChevronDown from '../../../components/icons/arrows/ChevronDown'
import {remCalc} from '../../../theme/_utils'

/** Map of refinement attribute IDs to the components used to display values as filter options. */
export const componentMap = {
    cgid: LinkRefinements,
    c_hexColour: ColorRefinements,
    c_size: SizeRefinements,
    price: RadioRefinements,
}

const InlineRefinements = ({
    itemsBefore,
    itemsAfter,
    excludedFilters = [],
    filters = [],
    toggleFilter,
    selectedFilters,
    setDisplayAllFilters = noop,
    isLoading = false,
}) => {
    // Exclude filters in the exclude list.
    if (excludedFilters) {
        filters = filters.filter(({attributeId}) => !excludedFilters.includes(attributeId))
    }

    // Getting the indices of filters to open accordions by default
    let filtersIndexes = filters?.map((filter, idx) => idx)
    const styles = useMultiStyleConfig('Filters')
    const containerRef = useRef(null)

    useEffect(() => {
        const checkOverFlow = () => {
            if (containerRef.current) {
                const {scrollHeight, clientHeight} = containerRef.current
                const isOverflowing = scrollHeight > clientHeight
                setDisplayAllFilters(!isOverflowing)
            }
        }

        checkOverFlow()

        window.addEventListener('resize', checkOverFlow)

        return () => window.removeEventListener('resize', checkOverFlow)
    }, [filters])

    return (
        <Stack spacing={8}>
            {/* Wait to have filters before rendering the Accordion to allow the deafult indexes to be accurate */}
            {isLoading && (
                <Stack gap={remCalc(16)}>
                    <Flex
                        maxWidth={'100%'}
                        gap={remCalc(16)}
                        overflow="hidden"
                        height={remCalc(60)}
                    >
                        <Skeleton height={remCalc(60)} width={remCalc(120)} />
                        <Skeleton height={remCalc(60)} width={remCalc(120)} />
                        <Skeleton height={remCalc(60)} width={remCalc(120)} />
                        <Skeleton height={remCalc(60)} width={remCalc(120)} />
                        <Skeleton height={remCalc(60)} width={remCalc(120)} />
                        <Skeleton height={remCalc(60)} width={remCalc(120)} />
                        <Skeleton height={remCalc(60)} width={remCalc(120)} />
                        <Skeleton height={remCalc(60)} width={remCalc(120)} />
                    </Flex>
                </Stack>
            )}
            {!isLoading && filtersIndexes && (
                <HStack gap={remCalc(16)}>
                    <Flex
                        ref={containerRef}
                        maxWidth={remCalc(1200)}
                        gap={remCalc(16)}
                        flexWrap="wrap"
                        overflow="hidden"
                        height={remCalc(60)}
                    >
                        {itemsAfter.filter((item) => item.key === 'sortby') || null}
                        {itemsBefore}

                        {filters?.map((filter, idx) => {
                            // Render the appropriate component for the refinement type, fallback to checkboxes
                            const Values = componentMap[filter.attributeId] || CheckboxRefinements
                            let selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []

                            // Catch any non-array values and wrap them in an array
                            if (!Array.isArray(selectedFiltersArray)) {
                                selectedFiltersArray = [selectedFiltersArray]
                            }
                            if (filter.values) {
                                return (
                                    <Menu key={idx}>
                                        {({isOpen}) => (
                                            <>
                                                <MenuButton
                                                    as={Button}
                                                    rightIcon={
                                                        <ChevronDown
                                                            {...styles.filterIcon}
                                                            style={{
                                                                transform: isOpen
                                                                    ? 'rotate(180deg)'
                                                                    : 'rotate(0deg)',
                                                            }}
                                                        />
                                                    }
                                                    {...styles.filterButton}
                                                    {...styles.refinementsSelectorButton}
                                                >
                                                    <Text {...styles.filterText}>
                                                        {filter.label}
                                                    </Text>
                                                </MenuButton>
                                                <MenuList {...styles.filterOpenMenu}>
                                                    <Values
                                                        selectedFilters={selectedFiltersArray}
                                                        filter={filter}
                                                        toggleFilter={toggleFilter}
                                                    />
                                                </MenuList>
                                            </>
                                        )}
                                    </Menu>
                                )
                            } else {
                                return null
                            }
                        })}
                    </Flex>
                    {/* {itemsAfter} */}
                    {itemsAfter.filter((item) => item.key !== 'sortby')}
                </HStack>
            )}
        </Stack>
    )
}

InlineRefinements.propTypes = {
    itemsBefore: PropTypes.arrayOf(PropTypes.element),
    itemsAfter: PropTypes.arrayOf(PropTypes.element),
    filters: PropTypes.array,
    excludedFilters: PropTypes.arrayOf(PropTypes.string),
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.object,
    isLoading: PropTypes.bool,
    numberOfFiltersToRender: PropTypes.number,
    setDisplayAllFilters: PropTypes.func,
}

export default InlineRefinements
